import { useSortable } from '@dnd-kit/sortable';
import React, { useContext, useMemo } from 'react';
import { CSS } from '@dnd-kit/utilities';
import { Button } from 'antd';
import DragTableRowIcon from 'assets/img/drag-table-row.svg?react';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';

import s from './styles.module.scss';

import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

export interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

export const RowContext = React.createContext<RowContextProps>({});

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

export const Row: React.FC<RowProps> = ({
  'data-row-key': dataRowKey,
  ...props
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dataRowKey,
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'default',
    height: 48,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

export const DragHandle: React.FC<{ name: string; guid: string }> = ({
  name,
  guid,
}) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);

  return (
    <div className={s['drag-handle-col']}>
      <Button
        type="text"
        icon={<DragTableRowIcon width={24} height={24} />}
        style={{ cursor: 'move' }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
      <Link to={Routes.patientGuid(guid)}>{name}</Link>
    </div>
  );
};
